import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import API from '../../../services/API'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AnDateTimePicker from '../../../components/DateTime/DateTimePicker'
import VDICheckForm from '../../governance/cleaningSchedules/VehicleCleanForm/VDICheckForm'
import SmallModal from '../../../components/Modals/SmallModal'
import Lottie from 'lottie-react'
import successLottie from '../../../assets/lottie/formSubmission2.json'
import { useNavigate } from 'react-router-dom'
import DrugBags from './DrugsBags'
import Attachments from '../../../components/Attachments/Attachments'
import { GlobalContext } from '../../../App'
import { LinearProgress } from '@mui/material'
import Loading from '../../loading/loadingPage'

const VDIForm = () => {
  const [vehicleOptions, setVehicleOptions] = useState([])
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [vdiData, setVdiData] = useState({})
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [vdiTime, setVDITime] = useState(moment())
  const [fuelAmount, setFuelAmount] = useState('')
  const [mileage, setMileage] = useState('')
  const [notes, setNotes] = useState('')
  const [defectReportRequired, setDefectReportRequired] = useState(false)
  const [vehicleUseable, setVehicleUseable] = useState(true)
  const [drugBags, setDrugBags] = useState([])
  const [vehicleOOSError, setVehicleOOSError] = useState(false)
  const [attachments, setAttachments] = useState([])
  const [loadingForm, setLoadingForm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [slowWarning, setSlowWarning] = useState(false)
  const [canRetry, setCanRetry] = useState(false)

  const navigate = useNavigate()

  useEffect(() => {
    API.get('/vehicleList')
      .then(
        (res) =>
          res.status === 200 &&
          setVehicleOptions(res.data.filter((x) => x.vehicleType)),
      )
      .catch((e) => console.log(e))
  }, [])

  const somethingNotComplete = Object.keys(vdiData).some((x) =>
    vdiData[x].some((y) => y.value !== false && y.value !== true),
  )

  const somethingWrong = Object.keys(vdiData).some((x) =>
    vdiData[x].some((y) => y.value === false),
  )

  useEffect(() => {
    if (selectedVehicle) {
      setLoadingForm(true)
      API.post('/getvdioptions', {
        vehicleType: selectedVehicle.vehicleType,
      }).then((res) => {
        if (res.status === 200) {
          setVdiData(res.data)
          setLoadingForm(false)
        }
      })
    }
  }, [selectedVehicle])

  const handleSubmit = async (e) => {
    e.preventDefault()

    setIsSubmitting(true)
    setSlowWarning(false)
    setCanRetry(false)
    setVehicleOOSError(false)

    const controller = new AbortController()

    const slowTimer = setTimeout(() => {
      setSlowWarning(true)
    }, 2000)

    const failTimer = setTimeout(() => {
      controller.abort()
      setCanRetry(true)
    }, 8000)

    try {
      const res = await API.post(
        '/vehicles/vdi',
        {
          checks: vdiData,
          vehicle: selectedVehicle._id,
          date: vdiTime,
          fuelAmount,
          mileage,
          notes,
          vehicleUseable,
          defectReportRequired,
          drugBags,
          attachments,
        },
        {
          signal: controller.signal,
        },
      )

      if (res.status === 200) {
        setShowSuccessMessage(true)
      }
    } catch (error) {
      console.error(error)

      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        setSlowWarning(true)
        setCanRetry(true)
        return
      }

      if (
        !navigator.onLine ||
        error.message === 'Network Error' ||
        error.code === 'ERR_NETWORK' ||
        !error.response
      ) {
        setSlowWarning(true)
        setCanRetry(true)
        return
      }

      if (
        error.response?.status === 400 &&
        error.response?.data === 'Vehicle Out of Service'
      ) {
        setVehicleOOSError(true)
        return
      }

      setCanRetry(true)
    } finally {
      clearTimeout(slowTimer)
      clearTimeout(failTimer)
      setIsSubmitting(false)
    }
  }

  const sortedVehicleOptions = [...vehicleOptions].sort((a, b) => {
    // 1. Group sort — must match groupBy
    const typeCompare = a.vehicleType.localeCompare(b.vehicleType)
    if (typeCompare !== 0) return typeCompare

    // 2. Within-group sort by callSign
    return a.callSign.localeCompare(b.callSign)
  })

  return (
    <Stack gap={1} component="form" onSubmit={handleSubmit}>
      <Stack gap={1} component={Paper} padding={1}>
        <Typography variant="title2">Record VDI</Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} lg={3}>
            <Autocomplete
              options={sortedVehicleOptions}
              disableClearable
              getOptionLabel={(opt) =>
                opt?.status === 'Out of Service'
                  ? `${opt.callSign} - ${opt.status}`
                  : (opt?.callSign ?? '')
              }
              renderOption={(props, option) => {
                const { key, ...optionProps } = props
                return (
                  <li key={key} {...optionProps}>
                    <Typography
                      sx={{
                        color:
                          option.status === 'Out of Service'
                            ? 'anRed.main'
                            : 'anBlue.main',
                        fontWeight:
                          option.status === 'Out of Service'
                            ? 'inherit'
                            : 'bold',
                      }}
                    >
                      {option.callSign}
                      {option.status === 'Out of Service' &&
                        ` - ${option.status}`}
                    </Typography>
                  </li>
                )
              }}
              onChange={(e, value) => {
                setSelectedVehicle(value)
                vehicleOOSError && setVehicleOOSError(false)
              }}
              value={selectedVehicle}
              isOptionEqualToValue={(option, value) =>
                option?._id === value?._id
              }
              groupBy={(opt) => opt.vehicleType}
              getOptionDisabled={(opt) => opt.status === 'Out of Service'}
              color="error"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vehicle"
                  required
                  autoComplete="off"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <AnDateTimePicker
              fullWidth
              date={vdiTime}
              onChange={(value) => setVDITime(value)}
              label="VDI Date/Time"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth>
              <InputLabel id="fuelAmount-label">Fuel Amount</InputLabel>
              <Select
                labelId="fuelAmount-label"
                label="Fuel Amount"
                value={fuelAmount}
                onChange={(e) => setFuelAmount(e.target.value)}
                required
              >
                <MenuItem value="Empty">Empty</MenuItem>
                <MenuItem value="1/4">1/4</MenuItem>
                <MenuItem value="1/2">1/2</MenuItem>
                <MenuItem value="3/4">3/4</MenuItem>
                <MenuItem value="Full">Full</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <TextField
              value={mileage}
              onChange={(e) => setMileage(e.target.value)}
              type="number"
              required
              fullWidth
              label="Mileage"
              autoComplete="off"
            />
          </Grid>
        </Grid>
      </Stack>
      {loadingForm && <Loading hideText />}
      <VDICheckForm
        data={vdiData}
        setData={setVdiData}
        selectedVehicleType={selectedVehicle?.vehicleType}
      />
      {!loadingForm && selectedVehicle && (
        <>
          <Stack component={Paper} padding={1} gap={1}>
            <Typography variant="title2">Summary</Typography>
            <TextField
              label="Notes"
              value={notes}
              multiline
              minRows={2}
              onChange={(e) => setNotes(e.target.value)}
              required={somethingWrong}
              helperText={
                somethingWrong && 'Required when something is marked is a fault'
              }
            />
            {somethingWrong && (
              <>
                <FormControl>
                  <InputLabel>
                    Does this VDI contain defects that need reporting?
                  </InputLabel>
                  <Select
                    label="Does this VDI contain defects that need reporting?"
                    value={defectReportRequired}
                    onChange={(e) => setDefectReportRequired(e.target.value)}
                  >
                    <MenuItem value={false}>No</MenuItem>
                    <MenuItem value={true}>Yes</MenuItem>
                  </Select>
                </FormControl>
                <FormControl>
                  <InputLabel>
                    Is the vehicle safe to continue using?
                  </InputLabel>
                  <Select
                    label="Is the vehicle safe to continue using?"
                    value={vehicleUseable}
                    onChange={(e) => setVehicleUseable(e.target.value)}
                  >
                    <MenuItem value={true}>Yes</MenuItem>
                    <MenuItem value={false}>No</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </Stack>

          <Stack component={Paper} padding={1} gap={1}>
            <Attachments
              s3folder="fleetequipment/vdi-attachments"
              actionAfterUpload={(s3key) =>
                setAttachments((prev) => [...prev, s3key])
              }
              actionAfterDelete={() => {
                // Nothing to do yet
              }}
            />
          </Stack>
          {/* <DrugBags drugBags={drugBags} setDrugBags={setDrugBags} /> */}
          {/* TODO */}
          <Stack gap={1} component={Paper} padding={2}>
            {somethingNotComplete && (
              <Alert severity="info">
                All items needs to be marked as complete or incomplete
              </Alert>
            )}
            <Button
              fullWidth
              color="success"
              variant="contained"
              type="submit"
              disabled={
                somethingNotComplete ||
                Object.keys(vdiData).length === 0 ||
                isSubmitting
              }
            >
              {isSubmitting
                ? 'Submitting...'
                : canRetry
                  ? 'Try Submitting Again'
                  : 'Submit'}
            </Button>
            {slowWarning && (
              <Typography variant="caption" color="error">
                This is taking longer than usual. Your internet connection may
                be slow or unstable.
              </Typography>
            )}
            {isSubmitting && <LinearProgress />}
          </Stack>
        </>
      )}
      <SmallModal open={showSuccessMessage}>
        <Stack>
          <Typography variant="title2" fontSize="1em" textAlign="center">
            VDI Recorded
          </Typography>
          <Lottie animationData={successLottie} loop={false} />
          <Button
            onClick={() => navigate('/')}
            variant="contained"
            size="small"
            color="anBlue"
          >
            Return Home
          </Button>
        </Stack>
      </SmallModal>
    </Stack>
  )
}

export default VDIForm
